import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { FileText, Hourglass, Network, Scale, type LucideIcon } from 'lucide-react';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import { getCaseSettings, type HrCaseSettings } from '@/api/services/hrCases.service';
import { listTemplates } from '@/api/services/hrDocuments.service';
import { listHrStructure } from '@/api/services/hr-structure.service';
import { getProbationPolicy, type ProbationPolicy } from '@/api/services/probation.service';
import { CompanyStructureTab } from '@/features/companies/components/CompanyStructureTab';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { CaseSettingsDialog } from '../cases/CaseSettingsDialog';
import { TemplatesPanel } from '../documents/HrDocumentsTab';
import { HrAreaHeader, HrCompanySelect, SetupSectionIntro } from '../hrShared';
import { ProbationPolicyDialog } from '../probation/ProbationPolicyDialog';
import { describeReminderDays, normalizePolicy } from '../probation/probationFormat';

type SetupSection = 'structure' | 'templates' | 'probation' | 'cases';

const plural = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/** One line per section saying how far the company has got, shown on its tab. */
function useSectionStatus(
  companyId: string,
  allowed: Set<SetupSection>
): Record<SetupSection, string | undefined> {
  const enabled = (section: SetupSection): boolean => companyId !== '' && allowed.has(section);
  const structure = useQuery({
    queryKey: ['hr-structure', companyId],
    queryFn: () => listHrStructure(companyId),
    enabled: enabled('structure'),
  });
  const templates = useQuery({
    queryKey: ['hr-document-templates', companyId],
    queryFn: () => listTemplates(companyId),
    enabled: enabled('templates'),
  });
  const probation = useQuery({
    queryKey: ['hr-probation-policy', companyId],
    queryFn: () => getProbationPolicy(companyId),
    enabled: enabled('probation'),
  });
  const cases = useQuery({
    queryKey: ['hr-case-settings', companyId],
    queryFn: () => getCaseSettings(companyId),
    enabled: enabled('cases'),
  });

  const departments = Array.isArray(structure.data)
    ? structure.data.filter((item) => item.kind === 'department' && item.active).length
    : undefined;
  const ready = Array.isArray(templates.data)
    ? templates.data.filter((template) => template.currentVersion).length
    : undefined;
  return {
    structure:
      departments === undefined
        ? undefined
        : departments === 0
          ? 'Not started'
          : plural(departments, 'department'),
    templates:
      ready === undefined
        ? undefined
        : templates.data?.length === 0
          ? 'Not started'
          : `${ready} ready to use`,
    probation: probation.data
      ? probation.data.isDefault
        ? 'Standard rules'
        : 'Custom rules'
      : undefined,
    cases: cases.data ? (cases.data.isDefault ? 'Standard settings' : 'Custom settings') : undefined,
  };
}

/** A labelled fact in a settings summary. */
function Fact({ term, children }: { term: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-600 dark:text-gray-300">{term}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function LoadState({
  isLoading,
  error,
  label,
}: {
  isLoading: boolean;
  error: unknown;
  label: string;
}): React.ReactElement | null {
  if (isLoading) {
    return (
      <Card contentClassName="flex justify-center p-10">
        <div role="status">
          <Spinner />
          <span className="sr-only">Loading {label}</span>
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <p className="text-sm text-danger" role="alert">
          {getErrorMessage(error)}
        </p>
      </Card>
    );
  }
  return null;
}

function RulesBadge({ isDefault, noun }: { isDefault: boolean; noun: string }): React.ReactElement {
  return (
    <Badge variant={isDefault ? 'neutral' : 'info'} size="sm">
      {isDefault ? `Standard ${noun}` : `Custom ${noun}`}
    </Badge>
  );
}

function ProbationSummary({ policy }: { policy: ProbationPolicy }): React.ReactElement {
  return (
    <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
      <Fact term="Length">{plural(policy.durationDays, 'day')} from the joining date</Fact>
      <Fact term="Extensions">
        {policy.maxExtensions === 0
          ? 'Not allowed'
          : `Up to ${policy.maxExtensions}, each up to ${plural(policy.maxExtensionDays, 'day')}`}
      </Fact>
      <Fact term="Manager review">
        {policy.reviewRequired ? 'Required before confirming' : 'Optional'}
      </Fact>
      <Fact term="Reminders">{describeReminderDays(policy.reminderDaysBefore)}</Fact>
      <Fact term="Escalation">
        Organization admins are told after {plural(policy.escalateAfterDaysOverdue, 'day')} overdue
      </Fact>
    </dl>
  );
}

function ProbationRules({ companyId }: { companyId: string }): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-probation-policy', companyId],
    queryFn: () => getProbationPolicy(companyId),
  });
  const policy = data ? normalizePolicy(data, companyId) : undefined;
  return (
    <div className="space-y-4">
      <SetupSectionIntro
        badge={policy && <RulesBadge isDefault={policy.isDefault} noun="rules" />}
        action={
          <Button size="sm" variant="secondary" disabled={!policy} onClick={() => setEditing(true)}>
            Edit rules
          </Button>
        }
      >
        How long new joiners stay on probation and what happens near the end. Each probation keeps
        the rules it started with, so changes apply to probations that start afterwards.
      </SetupSectionIntro>
      <LoadState isLoading={isLoading} error={error} label="probation rules" />
      {policy && (
        <Card contentClassName="p-5">
          <ProbationSummary policy={policy} />
        </Card>
      )}
      {editing && policy && (
        <ProbationPolicyDialog
          companyId={companyId}
          policy={policy}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function OptionChips({
  options,
}: {
  options: HrCaseSettings['disciplineCaseTypes'];
}): React.ReactElement {
  const active = options.filter((option) => option.active);
  const off = options.length - active.length;
  return (
    <span className="flex flex-wrap gap-1.5">
      {active.map((option) => (
        <Badge key={option.key} variant="neutral" size="sm">
          {option.label}
        </Badge>
      ))}
      {off > 0 && (
        <span className="text-xs text-gray-600 dark:text-gray-300">+{off} turned off</span>
      )}
    </span>
  );
}

function CaseSummary({ settings }: { settings: HrCaseSettings }): React.ReactElement {
  const levels = [...settings.warningLevels].sort((a, b) => a.rank - b.rank);
  return (
    <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
      <Card contentClassName="space-y-5 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Discipline</h3>
        <dl className="space-y-5">
          <Fact term="Case types">
            <OptionChips options={settings.disciplineCaseTypes} />
          </Fact>
          <Fact term="Warning levels">
            <ol className="mt-1 max-w-md space-y-1.5">
              {levels.map((level, index) => (
                <li key={level.key} className="flex items-baseline gap-2">
                  <span className="w-4 shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">{level.label}</span>
                  <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300">
                    expires after {plural(level.expiryDays, 'day')}
                  </span>
                </li>
              ))}
            </ol>
          </Fact>
          <div className="grid gap-5 sm:grid-cols-3">
            <Fact term="Hearing notice">{plural(settings.hearingNoticeDays, 'day')}</Fact>
            <Fact term="Appeal window">{plural(settings.appealWindowDays, 'day')}</Fact>
            <Fact term="Next level suggested after">
              {plural(settings.activeWarningsBeforeNextLevel, 'warning')}
            </Fact>
          </div>
        </dl>
      </Card>
      <Card contentClassName="space-y-5 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Grievances</h3>
        <dl className="space-y-5">
          <Fact term="Categories">
            <OptionChips options={settings.grievanceCategories} />
          </Fact>
          <Fact term="Acknowledge within">{plural(settings.acknowledgeWithinDays, 'day')}</Fact>
        </dl>
      </Card>
    </div>
  );
}

function CaseRules({ companyId }: { companyId: string }): React.ReactElement {
  const { can } = usePermissions();
  const [editing, setEditing] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-case-settings', companyId],
    queryFn: () => getCaseSettings(companyId),
  });
  return (
    <div className="space-y-4">
      <SetupSectionIntro
        badge={data && <RulesBadge isDefault={data.isDefault} noun="settings" />}
        action={
          <Button size="sm" variant="secondary" disabled={!data} onClick={() => setEditing(true)}>
            Edit settings
          </Button>
        }
      >
        The choices people pick from when HR opens a discipline case or an employee raises a
        grievance, and the time limits each one follows. Cases already open keep the settings
        they started with.
      </SetupSectionIntro>
      <LoadState isLoading={isLoading} error={error} label="case settings" />
      {data && <CaseSummary settings={data} />}
      {editing && data && (
        <CaseSettingsDialog
          companyId={companyId}
          settings={data}
          canEditDiscipline={can('hr', 'discipline')}
          canEditGrievances={can('hr', 'grievances')}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

const SECTIONS: Array<{
  value: SetupSection;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    value: 'structure',
    label: 'Departments & jobs',
    description: 'Departments, teams, job titles and grades',
    icon: Network,
  },
  {
    value: 'templates',
    label: 'Letter templates',
    description: 'Approved wording for HR letters',
    icon: FileText,
  },
  {
    value: 'probation',
    label: 'Probation rules',
    description: 'Length, extensions and reminders',
    icon: Hourglass,
  },
  {
    value: 'cases',
    label: 'Case settings',
    description: 'Discipline and grievance options',
    icon: Scale,
  },
];

/**
 * The lists and rules each company configures for itself. Unlike the other
 * areas this works one company at a time, so it follows the filter when the
 * filter holds one company and asks otherwise.
 */
export function HrSetupTab({
  companyIds,
  companies,
}: {
  companyIds?: string[];
  companies: Array<{ _id: string; name: string }>;
}): React.ReactElement {
  const { can } = usePermissions();
  const [searchParams] = useSearchParams();
  const access: Record<SetupSection, boolean> = {
    structure: can('hr', 'structure'),
    templates: can('hr', 'documents'),
    probation: can('hr', 'probation'),
    cases: can('hr', 'discipline') || can('hr', 'grievances'),
  };
  const allowed = SECTIONS.filter((section) => access[section.value]);
  const [requested, setRequested] = useState<SetupSection | null>(
    () => searchParams.get('section') as SetupSection | null
  );
  const section = allowed.find((item) => item.value === requested)?.value ?? allowed[0]?.value;
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const inFilter = companyIds?.length
    ? companies.filter((company) => companyIds.includes(company._id))
    : companies;
  const [picked, setPicked] = useState('');
  const companyId = inFilter.some((company) => company._id === picked)
    ? picked
    : (inFilter[0]?._id ?? '');
  const status = useSectionStatus(companyId, new Set(allowed.map((item) => item.value)));

  const moveTab = (event: React.KeyboardEvent, index: number): void => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + allowed.length) % allowed.length;
    setRequested(allowed[next].value);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="space-y-5">
      <HrAreaHeader
        title="Setup"
        description="The lists and rules each company uses for HR. You usually set these up once per company, then adjust them now and then."
        controls={
          <HrCompanySelect
            id="hr-setup-company"
            label="Company being set up"
            companies={inFilter}
            value={companyId}
            onChange={setPicked}
          />
        }
      />
      <div
        role="tablist"
        aria-label="Setup section"
        className="grid grid-cols-2 gap-2 lg:grid-cols-4"
      >
        {allowed.map((item, index) => {
          const selected = section === item.value;
          const Icon = item.icon;
          return (
            <button
              key={item.value}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`hr-setup-tab-${item.value}`}
              aria-selected={selected}
              aria-controls="hr-setup-panel"
              aria-labelledby={`hr-setup-tab-${item.value}-label`}
              aria-describedby={`hr-setup-tab-${item.value}-status`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setRequested(item.value)}
              onKeyDown={(event) => moveTab(event, index)}
              className={cn(
                'flex min-w-0 items-start gap-3 rounded-lg border px-3.5 py-3 text-left',
                'transition-[background-color,border-color] duration-150 motion-reduce:transition-none',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                selected
                  ? 'border-primary/40 bg-primary/5 dark:border-primary/50 dark:bg-primary/10'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700/50'
              )}
            >
              <Icon
                aria-hidden
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  selected ? 'text-primary dark:text-sky-300' : 'text-gray-400 dark:text-gray-500'
                )}
              />
              <span className="min-w-0 flex-1">
                <span
                  id={`hr-setup-tab-${item.value}-label`}
                  className={cn(
                    'block text-sm font-medium',
                    selected ? 'text-primary dark:text-sky-200' : 'text-gray-900 dark:text-gray-100'
                  )}
                >
                  {item.label}
                </span>
                <span className="mt-0.5 hidden text-xs text-gray-600 sm:block dark:text-gray-300">
                  {item.description}
                </span>
                <span
                  id={`hr-setup-tab-${item.value}-status`}
                  className="mt-1.5 block min-h-4 text-xs font-medium text-gray-700 dark:text-gray-200"
                >
                  {status[item.value]}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div
        id="hr-setup-panel"
        role="tabpanel"
        aria-labelledby={section ? `hr-setup-tab-${section}` : undefined}
        className="min-w-0"
      >
        {companyId && section === 'structure' && (
          <CompanyStructureTab key={companyId} companyId={companyId} />
        )}
        {companyId && section === 'templates' && (
          <TemplatesPanel key={companyId} companyId={companyId} />
        )}
        {companyId && section === 'probation' && (
          <ProbationRules key={companyId} companyId={companyId} />
        )}
        {companyId && section === 'cases' && <CaseRules key={companyId} companyId={companyId} />}
      </div>
    </div>
  );
}
